import moment from "moment";

/**
 * Working days, and the period a goal is FOR.
 *
 * Lifted out of components/ProfitTrend/data.js when Goal Progress grew a pace
 * line of its own, so the two cards divide a goal by the SAME denominator.
 * ProfitTrend still re-exports all three names (its own callers and tests
 * import them from there), but there is only one definition, here — a second,
 * nearly-right one is exactly how two cards end up quoting different
 * working-day counts for one month.
 */

/**
 * The company's own holidays as a set of YYYY-MM-DD, however they arrive.
 * The company saga has already dropped any that fall on a weekend, and it
 * parses them as UTC — parsed any other way a holiday stored at midnight UTC
 * slides onto the day before for anyone west of Greenwich.
 */
export const holidayDaySet = (holidays) => {
  const out = new Set();
  (holidays || []).forEach((h) => {
    const d = moment.utc(h?.date ?? h);
    if (d.isValid()) out.add(d.format("YYYY-MM-DD"));
  });
  return out;
};

/**
 * Working days in a range: Monday–Friday, minus the company's holidays.
 *
 * Deliberately the SAME definition the company calendar counts with
 * (components/Calendar/CalendarContainer.jsx) and the Yeti scales its pace
 * targets by (yetiBusinessDayCounts in hooks/company.js).
 *
 * Revenue does land on the odd weekend, and that is not a contradiction: this
 * counts the days the company PLANNED to trade, not the days money is allowed
 * to arrive on. A Saturday's takings put the month ahead of pace rather than
 * changing what the pace is.
 */
export const workingDaysBetween = (from, to, holidays) => {
  const skip = holidays instanceof Set ? holidays : holidayDaySet(holidays);
  const cursor = moment(from).startOf("day");
  const end = moment(to).endOf("day");
  if (!cursor.isValid() || !end.isValid() || end.isBefore(cursor)) return 0;
  let n = 0;
  /* Same hard stop bucketsFor uses - a malformed range must never spin. */
  for (let guard = 0; cursor.isSameOrBefore(end, "day") && guard < 4000; guard += 1) {
    const dow = cursor.day();
    if (dow > 0 && dow < 6 && !skip.has(cursor.format("YYYY-MM-DD"))) n += 1;
    cursor.add(1, "day");
  }
  return n;
};

/**
 * The end of the period a GOAL is for, which is not where the figures stop.
 *
 * `to` is where the figures stop (today, for any window that includes it).
 * This is how long the period the goal was set for actually runs, and it is
 * what a goal has to be divided by: "$250,000 this month" is not missed on
 * the 10th, and "$250,000 this week" is not a Monday's target.
 *
 * Windows that are already whole (yesterday, last week, last month) end where
 * they end. A custom range keeps the end the user typed, which resolveWindow
 * clamps to today for the figures but which is still the period they meant.
 */
export const periodEndFor = (bucket, to, custom = null) => {
  const end = moment(to);
  if (!end.isValid()) return end;
  if (bucket === "week") return end.clone().endOf("week");
  if (bucket === "month") return end.clone().endOf("month");
  if (bucket === "quarter") return end.clone().endOf("quarter");
  if (bucket === "ytd" || bucket === "year") return end.clone().endOf("year");
  if (bucket === "custom") {
    const typed = custom?.to ? moment(custom.to).endOf("day") : null;
    return typed && typed.isValid() && typed.isAfter(end) ? typed : end;
  }
  return end;
};

/**
 * What one period of this window is called - "month", "week", "quarter".
 *
 * The pace LINE is a daily figure, but the goal behind it is not, and the
 * legend names the goal: "Pace $200,000/month" is the target everyone
 * recognises, where "$9,091/day" is arithmetic they have to undo. Null for a
 * custom range, which is nobody's idea of a repeating period - the header's
 * own dates say what it covers.
 */
export const periodWordFor = (bucket) => {
  if (bucket === "today" || bucket === "yesterday") return "day";
  if (bucket === "week" || bucket === "lastWeek") return "week";
  if (bucket === "quarter") return "quarter";
  if (bucket === "ytd" || bucket === "year") return "year";
  if (bucket === "custom") return null;
  return "month";
};

/**
 * One row per calendar day from the period's start to the period's END —
 * which is NOT where the figures stop — carrying whether the company planned
 * to trade on it, whether it has happened yet, and what the goal expects by
 * then.
 *
 * `working` is the same Mon–Fri-minus-holidays test workingDaysBetween counts
 * with, decided once per day here so a caller can plot only the days the
 * company actually trades without re-deriving (or disagreeing about) the rule.
 *
 * `dailyPace` is the goal divided by the period's working days, and may be
 * null — a card with no goal still wants the calendar. When it is set:
 *
 *   • `pace`    — that flat per-day figure, carried across weekends too. The
 *                 daily view's line answers "what does a day of trading have
 *                 to bring", and a sawtooth dropping to zero every Saturday
 *                 would be unreadable next to the bars. Same call ProfitTrend
 *                 made for its own day-grain pace line.
 *   • `cumPace` — the pace accumulated over the working days GONE BY, so the
 *                 line climbs on weekdays, sits flat over a weekend or a
 *                 holiday, and lands exactly ON the goal on the period's last
 *                 working day. That identity is what makes the running total
 *                 crossing it mean "we are ahead", and it only holds because
 *                 the divisor and this sum count the same days.
 *
 * Rows past `to` are flagged `future`: the chart leaves their actuals null
 * (never 0 — zero is a claim that nothing was earned) so the trend stops at
 * today and the pace line runs on alone to the end of the month, which is
 * what makes "behind, with nine days left" visible.
 */
export const periodDaysFor = (from, periodEnd, to, holidays, dailyPace = null) => {
  if (!from || !periodEnd || !from.isValid?.() || !periodEnd.isValid?.()) return [];
  const paced = dailyPace > 0 ? dailyPace : null;
  const skip = holidays instanceof Set ? holidays : holidayDaySet(holidays);
  const cursor = from.clone().startOf("day");
  const end = periodEnd.clone().startOf("day");
  const lastReal = to && to.isValid?.() ? to.clone().startOf("day") : null;
  const out = [];
  let worked = 0;
  /* Same 400-day stop dailySeriesFor uses — the widest window this card
     offers (a year) is 366 rows, and a malformed range must never spin. */
  for (let guard = 0; cursor.isSameOrBefore(end, "day") && guard < 400; guard += 1) {
    const date = cursor.format("YYYY-MM-DD");
    const dow = cursor.day();
    const working = dow > 0 && dow < 6 && !skip.has(date);
    if (working) worked += 1;
    out.push({
      date,
      working,
      future: !!lastReal && cursor.isAfter(lastReal, "day"),
      pace: paced,
      cumPace: paced == null ? null : paced * worked,
    });
    cursor.add(1, "day");
  }
  return out;
};

/**
 * What a metric took on days the company doesn't trade on — the weekend and
 * holiday total, summed off the metric's own per-day series against a calendar
 * from periodDaysFor.
 *
 * Goal Progress needs it for one honest reason: its "working days only" axis
 * drops those columns, and in the DAY-BY-DAY view that takes their takings off
 * the chart with them. Rolling a Saturday's money onto the Monday bar would
 * make that bar a lie about Monday, so the card states the figure separately
 * instead and the bars plus this number reconcile to the period total. The
 * running-total view never needs it: hidden or not, every day is already inside
 * the total the line draws.
 *
 * A date the calendar doesn't mention is NOT counted — an empty or unresolved
 * calendar returns 0 rather than quietly reporting the whole period as off-days.
 */
export const offDayTotal = (series, periodDays) => {
  const working = new Set();
  let known = 0;
  (periodDays || []).forEach((d) => {
    if (!d || !d.date) return;
    known += 1;
    if (d.working) working.add(String(d.date));
  });
  if (!known) return 0;
  const inPeriod = new Set((periodDays || []).map((d) => String(d?.date)));
  return (series || []).reduce((sum, p) => {
    if (!p || !p.date) return sum;
    const key = String(p.date);
    if (!inPeriod.has(key) || working.has(key)) return sum;
    return sum + (Number(p.value) || 0);
  }, 0);
};
